import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export function FloatingActionBar({
    triggerElement,
    menuItems,
    position = "bottom-right",
    containerClassName = ""
}) {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const [pos, setPos] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (event) => {
            if (triggerRef.current && triggerRef.current.contains(event.target)) return;
            if (menuRef.current && menuRef.current.contains(event.target)) return;
            setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const toggleMenu = (e) => {
        if (!isOpen) {
            const rect = triggerRef.current.getBoundingClientRect();
            // Basic positioning logic; can be expanded
            let top = rect.bottom + window.scrollY;
            let left = rect.left + window.scrollX;

            if (position === "bottom-right") {
                left = rect.right + window.scrollX - 200; // Assuming ~200px menu width
            }

            setPos({ top, left });
        }
        setIsOpen(!isOpen);
    };

    return (
        <div className={`relative inline-block ${containerClassName}`}>
            <div ref={triggerRef} onClick={toggleMenu} className="cursor-pointer inline-flex">
                {triggerElement || (
                    <button className="text-slate-400 hover:text-white transition-colors">
                        <span className="material-symbols-outlined text-[18px]">more_vert</span>
                    </button>
                )}
            </div>

            {isOpen && createPortal(
                <div 
                    ref={menuRef}
                    className="fixed w-56 bg-slate-800 border border-slate-600 rounded-md shadow-2xl z-[9999] flex flex-col py-1 overflow-hidden"
                    style={{ top: pos.top + 4, left: pos.left }}
                >
                    {menuItems.map((item, idx) => {
                        if (item.type === 'divider') {
                            return <div key={`div-${idx}`} className="h-px bg-slate-700 my-1 w-full" />;
                        }
                        if (item.type === 'label') {
                            return <div key={`label-${idx}`} className="px-3 py-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{item.label}</div>;
                        }
                        if (item.type === 'custom') {
                            return <div key={`custom-${idx}`}>{item.render()}</div>;
                        }
                        
                        return (
                            <button
                                key={`item-${idx}`}
                                onClick={(e) => {
                                    if(item.keepOpen) e.stopPropagation();
                                    else setIsOpen(false);
                                    if(item.onClick) item.onClick(e);
                                }}
                                disabled={item.disabled}
                                className={`text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors w-full
                                    ${item.disabled ? 'text-slate-500 cursor-not-allowed' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}
                                    ${item.danger ? 'hover:bg-red-900/50 hover:text-red-400 text-red-400/80' : ''}
                                `}
                            >
                                {item.icon && <span className="material-symbols-outlined text-[16px]">{item.icon}</span>}
                                <span className="flex-1">{item.label}</span>
                                {item.toggle !== undefined && (
                                    <span className="material-symbols-outlined text-[16px] text-teal-400">
                                        {item.toggle ? 'toggle_on' : 'toggle_off'}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>,
                document.body
            )}
        </div>
    );
}
